/**
 * Article Detail JavaScript
 * Handles share, copy functionality, and AJAX comment submission
 */

class ArticleDetail {
  constructor() {
    this.shareButton = document.querySelector('[data-article-share]');
    this.copyButton = document.querySelector('[data-copy-link]');
    this.shareModal = document.querySelector('[data-share-modal]');
    this.openModalButton = document.querySelector('[data-open-share-modal]');
    this.closeModalButton = document.querySelector('[data-close-share-modal]');

    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Share button (native Web Share API)
    if (this.shareButton) {
      this.shareButton.addEventListener('click', () => this.handleShare());
    }

    // Copy link button
    if (this.copyButton) {
      this.copyButton.addEventListener('click', () => this.copyLink());
    }

    // Social share modal
    if (this.openModalButton && this.shareModal) {
      this.openModalButton.addEventListener('click', () => this.openShareModal());
    }
    if (this.closeModalButton && this.shareModal) {
      this.closeModalButton.addEventListener('click', () => this.closeShareModal());
    }
    if (this.shareModal) {
      this.shareModal.addEventListener('click', (e) => {
        if (e.target === this.shareModal) this.closeShareModal();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.shareModal.hidden) this.closeShareModal();
      });
    }

    // Handle scroll to comments after page reload (if form was submitted)
    this.handleCommentFormScroll();

    // Initialize carousel navigation
    this.initCarousel();
  }

  handleCommentFormScroll() {
    // Check if URL has the comment form hash (Shopify adds this after submission)
    if (window.location.hash === '#comment-form' || window.location.hash.includes('comment')) {
      const commentForm = document.getElementById('comments-form');
      if (commentForm) {
        // Use requestAnimationFrame to ensure smooth scroll after page load
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const offset = 100;
            const elementPosition = commentForm.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          });
        });
      }
    }
  }

  initCarousel() {
    const carouselContainer = document.querySelector('[data-carousel-container]');
    const prevButton = document.querySelector('[data-carousel-prev]');
    const nextButton = document.querySelector('[data-carousel-next]');

    if (!carouselContainer || !prevButton || !nextButton) return;

    const scrollAmount = 400; // Scroll by 400px

    // Function to update arrow visibility
    const updateArrows = () => {
      const scrollLeft = carouselContainer.scrollLeft;
      const scrollWidth = carouselContainer.scrollWidth;
      const clientWidth = carouselContainer.clientWidth;

      // Check if there's overflow content
      const hasOverflow = scrollWidth > clientWidth;

      if (!hasOverflow) {
        // No overflow, hide both arrows
        prevButton.style.display = 'none';
        nextButton.style.display = 'none';
        return;
      }

      // Show/hide prev button based on scroll position
      if (scrollLeft <= 10) {
        prevButton.style.display = 'none';
      } else {
        prevButton.style.display = 'flex';
      }

      // Show/hide next button based on scroll position
      if (scrollLeft + clientWidth >= scrollWidth - 10) {
        nextButton.style.display = 'none';
      } else {
        nextButton.style.display = 'flex';
      }
    };

    // Initial check
    updateArrows();

    // Update on scroll
    carouselContainer.addEventListener('scroll', updateArrows);

    // Update on window resize
    window.addEventListener('resize', updateArrows);

    // Click handlers
    prevButton.addEventListener('click', () => {
      carouselContainer.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    });

    nextButton.addEventListener('click', () => {
      carouselContainer.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    });
  }

  openShareModal() {
    if (this.shareModal) {
      this.shareModal.hidden = false;
      document.body.style.overflow = 'hidden';
    }
  }

  closeShareModal() {
    if (this.shareModal) {
      this.shareModal.hidden = true;
      document.body.style.overflow = '';
    }
  }

  async handleShare() {
    // Use native Web Share API
    if (navigator.share) {
      try {
        await navigator.share({
          title: document.title,
          text: document.querySelector('meta[name="description"]')?.content || '',
          url: window.location.href
        });
      } catch (err) {
        // User cancelled or error occurred
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
        }
      }
    } else {
      // If Web Share API not supported, just copy link
      this.copyLink();
    }
  }

  async copyLink() {
    const url = window.location.href;

    try {
      await navigator.clipboard.writeText(url);

      // Update button text
      const originalText = this.copyButton.innerHTML;
      this.copyButton.classList.add('copied');
      this.copyButton.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        Copied!
      `;

      // Reset after 2 seconds
      setTimeout(() => {
        this.copyButton.classList.remove('copied');
        this.copyButton.innerHTML = originalText;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      alert('Link copied to clipboard!');
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ArticleDetail();
  });
} else {
  new ArticleDetail();
}
